import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import apiClient from '../../api/client';
import { chemistryApi } from '../../api/chemistry';
import { ChemFile } from '../../types/chemistry';
import ProjectTargetUniverseSection from './ProjectTargetUniverseSection';

const BIOLOGY_EXT = new Set(['fasta', 'fa', 'fna', 'ffn', 'faa', 'frn', 'pdb']);

interface BiologyFileTarget {
  fileName: string;
  proteinName: string;
  uniprotId: string | null;
  extension: string;
}

/** Parse a biology filename to extract protein name and UniProt ID */
function parseBiologyFile(f: ChemFile): BiologyFileTarget {
  const rawName = (f.originalFileName || f.fileName).replace(/^[a-f0-9]{8}_/i, '');
  const ext = rawName.split('.').pop()?.toLowerCase() || '';
  const baseName = rawName.replace(/\.[^.]+$/, '');

  // Try to extract UniProt ID (pattern: P followed by 5 alphanumerics, e.g. P00533, Q9Y6K9)
  const uniprotMatch = baseName.match(/([A-Z][0-9][A-Z0-9]{3}[0-9])/);
  const uniprotId = uniprotMatch ? uniprotMatch[1] : null;

  // Extract protein name: part before underscore or the whole basename
  const parts = baseName.split('_');
  const proteinName = parts[0] || baseName;

  return { fileName: rawName, proteinName, uniprotId, extension: ext };
}

interface ProjectTargetsTabProps {
  projectId: number;
}

interface ProjectTarget {
  id: number;
  molecule_id: number;
  biologic_id: number;
  interaction_type: string | null;
  affinity_value: number | null;
  affinity_unit: string | null;
  binding_site: string | null;
  species: string | null;
  relationship_type: string | null;
  created_at: string | null;
  name: string;
  uniprot_id: string | null;
}

interface DiscoveredTarget {
  target_name: string;
  uniprot_id: string | null;
  activity_type: string | null;
  best_activity: number | null;
  activity_units: string | null;
  compound_count: number;
  source: string;
}

interface CachedDiscovery {
  targets: DiscoveredTarget[];
  compoundCount: number;
  analysedAt: number;
}

// Module-level caches — survive tab switches
const discoveryCache = new Map<number, CachedDiscovery>();

const formatTimeAgo = (timestamp: number): string => {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
};

const ProjectTargetsTab: React.FC<ProjectTargetsTabProps> = ({ projectId }) => {
  // ─── Biology files (FASTA, PDB, etc.) ─────────────────────────
  const [biologyFiles, setBiologyFiles] = useState<ChemFile[]>([]);
  const [isLoadingBiology, setIsLoadingBiology] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoadingBiology(true);
      try {
        const allFiles = await chemistryApi.getFiles(projectId);
        const bioFiles = allFiles.filter(f => {
          const ext = (f.originalFileName || f.fileName).split('.').pop()?.toLowerCase();
          return ext && BIOLOGY_EXT.has(ext);
        });
        if (!cancelled) setBiologyFiles(bioFiles);
      } catch { /* ignore */ }
      finally { if (!cancelled) setIsLoadingBiology(false); }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const parsedBiologyTargets = useMemo(() => biologyFiles.map(parseBiologyFile), [biologyFiles]);

  // ─── Project targets from DB ────────────────────────────────
  const [projectTargets, setProjectTargets] = useState<ProjectTarget[]>([]);
  const [isLoadingProject, setIsLoadingProject] = useState(true);

  // ─── Discovered targets (ChEMBL analysis) ───────────────────
  const cached = discoveryCache.get(projectId);
  const [discoveredTargets, setDiscoveredTargets] = useState<DiscoveredTarget[]>(cached?.targets ?? []);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [hasRun, setHasRun] = useState(!!cached);
  const [error, setError] = useState<string | null>(null);
  const [analysedAt, setAnalysedAt] = useState<number | null>(cached?.analysedAt ?? null);
  const [compoundCount, setCompoundCount] = useState(cached?.compoundCount ?? 0);
  const cancelledRef = useRef(false);
  const autoRunRef = useRef(false);
  const [, setTick] = useState(0);

  // Update "time ago" every 30s
  useEffect(() => {
    if (!analysedAt) return;
    const interval = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(interval);
  }, [analysedAt]);

  // ─── Restore discovered targets from localStorage on mount ──
  useEffect(() => {
    if (cached) return; // already have module-level cache
    try {
      const stored = localStorage.getItem(`targets_cache_${projectId}`);
      if (stored) {
        const data: CachedDiscovery = JSON.parse(stored);
        setDiscoveredTargets(data.targets);
        setCompoundCount(data.compoundCount);
        setAnalysedAt(data.analysedAt);
        setHasRun(true);
        discoveryCache.set(projectId, data);
      }
    } catch { /* ignore corrupt data */ }
  }, [projectId, cached]);

  // ─── Load project targets from DB ───────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoadingProject(true);
      try {
        const data = await chemistryApi.getProjectTargets(projectId);
        if (!cancelled) setProjectTargets(data.targets);
      } catch { /* ignore */ }
      finally { if (!cancelled) setIsLoadingProject(false); }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  // ─── ChEMBL discovery analysis ──────────────────────────────

  const handleCancel = useCallback(() => {
    cancelledRef.current = true;
  }, []);

  const runAnalysis = useCallback(async () => {
    cancelledRef.current = false;
    setIsRunning(true);
    setError(null);
    setDiscoveredTargets([]);

    try {
      const allFiles = await chemistryApi.getFiles(projectId);
      const chemFiles = allFiles.filter(f => f.moleculesLibraryCount > 0);

      if (chemFiles.length === 0) {
        setError('No compound files found in this project.');
        setIsRunning(false);
        return;
      }

      // Collect up to 20 SMILES from project files
      const smilesList: string[] = [];
      for (const file of chemFiles) {
        if (smilesList.length >= 20) break;
        const remaining = 20 - smilesList.length;
        const { molecules } = await chemistryApi.getMolecules(file.id, 1, remaining);
        for (const mol of molecules) {
          if (mol.smiles && smilesList.length < 20) {
            smilesList.push(mol.smiles);
          }
        }
      }

      if (cancelledRef.current) { setIsRunning(false); return; }

      setProgress({ current: 0, total: smilesList.length });

      // Single batch call — replaces 20 sequential /enrich/compound calls
      const res = await apiClient.post('/enrich/batch-targets', { smiles_list: smilesList });
      const data = res.data;

      const sorted: DiscoveredTarget[] = (data.targets || []).map((t: any) => ({
        target_name: t.target_name || 'Unknown',
        uniprot_id: t.uniprot_id || null,
        activity_type: t.activity_type || null,
        best_activity: t.activity_value ?? null,
        activity_units: t.activity_units || null,
        compound_count: t.compound_count || 0,
        source: t.source || 'ChEMBL',
      }));

      const now = Date.now();
      const analysedCount = data.compound_count || smilesList.length;
      const cacheData: CachedDiscovery = { targets: sorted, compoundCount: analysedCount, analysedAt: now };
      setDiscoveredTargets(sorted);
      setProgress({ current: smilesList.length, total: smilesList.length });
      setHasRun(true);
      setAnalysedAt(now);
      setCompoundCount(analysedCount);
      discoveryCache.set(projectId, cacheData);
      try { localStorage.setItem(`targets_cache_${projectId}`, JSON.stringify(cacheData)); } catch { /* ignore */ }
    } catch (err: any) {
      setError(err?.message || 'Target analysis failed');
    } finally {
      setIsRunning(false);
      cancelledRef.current = false;
    }
  }, [projectId]);

  // Auto-run discovery on mount if no cached data exists
  useEffect(() => {
    if (cached || hasRun || isRunning || autoRunRef.current) return;
    // Check localStorage first (handled by the restore effect above)
    try {
      const stored = localStorage.getItem(`targets_cache_${projectId}`);
      if (stored) return; // restore effect will populate state
    } catch { /* ignore */ }
    autoRunRef.current = true;
    runAnalysis();
  }, [projectId, cached, hasRun, isRunning, runAnalysis]);

  const formatActivity = (value: number | null, units: string | null) => {
    if (value == null) return '-';
    if (units === 'nM' || !units) return `${value.toFixed(1)} nM`;
    return `${value.toFixed(1)} ${units}`;
  };

  // ─── Main view ──────────────────────────────────────────────

  return (
    <div className="p-6 max-w-7xl mx-auto w-full">
      {/* ── Section 0: Project Targets (from biology files) ── */}
      {!isLoadingBiology && parsedBiologyTargets.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-3">
            Project Targets
            <span className="ml-2 text-sm font-normal text-gray-400">
              {parsedBiologyTargets.length} target{parsedBiologyTargets.length !== 1 ? 's' : ''} from uploaded files
            </span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {parsedBiologyTargets.map((bt, i) => (
              <div
                key={i}
                className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-800 dark:text-gray-200 truncate">{bt.proteinName}</span>
                      {bt.uniprotId && (
                        <a
                          href={`https://www.uniprot.org/uniprotkb/${bt.uniprotId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-500 hover:text-blue-600 hover:underline flex-shrink-0"
                        >
                          {bt.uniprotId}
                        </a>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5 truncate" title={bt.fileName}>
                      Source: {bt.fileName}
                    </p>
                    <span className="inline-block mt-1 px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded text-[10px] font-medium uppercase">
                      {bt.extension} file
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Target Universe (aggregated visualisation) ── */}
      <div className="mb-8">
        <div className="mb-4">
          <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200">Target Universe</h3>
          <p className="text-sm text-gray-400 mt-1">
            Aggregated target landscape across all project compounds — force graph, selectivity radar, and heatmap views.
          </p>
        </div>
        <ProjectTargetUniverseSection projectId={projectId} />
      </div>

      {/* ── Section 1: Your Targets (from DB) ── */}
      <div className="mb-8">
        <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-3">
          Your Targets
          {!isLoadingProject && projectTargets.length > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-400">{projectTargets.length} target{projectTargets.length !== 1 ? 's' : ''}</span>
          )}
        </h3>
        {isLoadingProject ? (
          <div className="flex items-center gap-3 py-4">
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-gray-500 dark:text-gray-400">Loading targets...</span>
          </div>
        ) : projectTargets.length > 0 ? (
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Target Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">UniProt ID</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Interaction</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Affinity</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Binding Site</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Species</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Relationship</th>
                </tr>
              </thead>
              <tbody>
                {projectTargets.map((t) => (
                  <tr key={t.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-200">{t.name}</td>
                    <td className="px-4 py-3">
                      {t.uniprot_id ? (
                        <a href={`https://www.uniprot.org/uniprot/${t.uniprot_id}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600 hover:underline">{t.uniprot_id}</a>
                      ) : <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{t.interaction_type || '-'}</td>
                    <td className="px-4 py-3 text-right font-mono text-gray-700 dark:text-gray-300">
                      {t.affinity_value != null ? `${t.affinity_value.toFixed(1)} ${t.affinity_unit || 'nM'}` : '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{t.binding_site || '-'}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{t.species || '-'}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded text-xs font-medium">{t.relationship_type || '-'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No manually linked targets yet. Link targets from individual compound views, or discover them below.
            </p>
          </div>
        )}
      </div>

      {/* ── Section 2: Discovered Targets (ChEMBL) ── */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200">Discovered Targets</h3>
            <p className="text-sm text-gray-400 mt-1">
              Protein targets identified from ChEMBL bioactivity data for your compounds.
            </p>
          </div>
          {!isRunning && (
            <button
              onClick={runAnalysis}
              className="px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              {hasRun ? 'Re-analyse' : 'Discover Targets'}
            </button>
          )}
        </div>

        {/* Running state */}
        {isRunning && (
          <div className="flex flex-col items-center justify-center py-10">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
            <span className="text-gray-600 dark:text-gray-300 font-medium text-sm">Discovering targets...</span>
            <p className="text-gray-400 text-xs mt-1 text-center max-w-sm">
              Analysing compounds against ChEMBL bioactivity data (first 20 compounds)
            </p>
            <div className="mt-3 w-56">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>Compound {progress.current} of {progress.total}</span>
                <span>{progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0}%</span>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-300"
                  style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
                />
              </div>
            </div>
            <button
              onClick={handleCancel}
              className="mt-4 px-4 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              Cancel
            </button>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}

        {!isRunning && hasRun && discoveredTargets.length === 0 && (
          <div className="py-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              No targets found from the first {compoundCount} compounds.
            </p>
            {analysedAt && (
              <p className="text-xs text-gray-400 mt-2">Analysed {formatTimeAgo(analysedAt)}</p>
            )}
          </div>
        )}

        {!isRunning && discoveredTargets.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                <span className="text-xl font-bold text-gray-800 dark:text-gray-200">{discoveredTargets.length}</span>
                <span className="ml-1">target{discoveredTargets.length !== 1 ? 's' : ''} discovered</span>
                <span className="ml-2 text-xs text-gray-400">(from first {compoundCount} compounds)</span>
                {analysedAt && (
                  <span className="ml-2 text-xs text-gray-400">&middot; {formatTimeAgo(analysedAt)}</span>
                )}
              </span>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Target Name</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">UniProt ID</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600 dark:text-gray-300"># Compounds</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Activity Type</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Best Activity</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {discoveredTargets.map((t, i) => (
                    <tr key={i} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-200">{t.target_name}</td>
                      <td className="px-4 py-3">
                        {t.uniprot_id ? (
                          <a href={`https://www.uniprot.org/uniprot/${t.uniprot_id}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600 hover:underline">{t.uniprot_id}</a>
                        ) : <span className="text-gray-400">-</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center justify-center min-w-[24px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs font-semibold">{t.compound_count}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{t.activity_type || '-'}</td>
                      <td className="px-4 py-3 text-right font-mono text-gray-700 dark:text-gray-300">{formatActivity(t.best_activity, t.activity_units)}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded text-xs font-medium">{t.source}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default ProjectTargetsTab;
